import { nonnull } from "@tento-core/assert";
import { Json, type Option } from "@tento-lona/sheets";
import {
  DateTime,
  PartialDate,
  TimeOfDay,
  TimezoneRegion,
} from "@tento-chrono";
import { AsyncStateManager } from "@tento-core/async-state-manager";
import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { Style, css } from "@tento-ui/component-styles";
import { $$ } from "@tento-ui/fastdom";
import { BarChartIcon, LonaIcons } from "@tento-ui/ui/icons";
import { SlotRow } from "@tento-ui/components/slot/slot-row";
import { SheetLegend } from "@tento-lona/sheets-ui/sheet/sheet-legend.ts";
import {
  type BindCellContext,
  type RenderPlugin,
  RowOptionsElement,
  type SheetLegendClass,
} from "@tento-lona/sheets-ui/sheet/render-plugin.ts";
import type { CellEntry, TimepointCellEntry } from "@tento-lona/sheets";
import { SheetDelegate } from "@tento-lona/sheets-ui/sheet/sheet-delegate.ts";
import { type SheetRow } from "@tento-lona";
import { COLUMN_SERIES_SUMMARY_SPEC } from "@tento-lona/sheets/platform";
import type { PerTypeSpec } from "@tento-lona/sheets/platform";

/** The renderer plugin that summarises a data row's series per column. */
export class ColumnSeriesSummaryRendererPlugin
  implements
    RenderPlugin.Renderer<DataSeriesCell, SheetLegend.DefaultRow>
{
  static readonly spec: PerTypeSpec = COLUMN_SERIES_SUMMARY_SPEC;

  readonly role = "renderer";
  readonly id = "column.series-summary";
  readonly icon: HTMLTemplateElement = LonaIcons.$template(BarChartIcon);

  #stateManager = new AsyncStateManager();

  constructor(public readonly makeRowOptions: RowOptionsElement.Factory) {}

  makeCell(): DataSeriesCell {
    return DataSeriesCell.make();
  }

  static readonly #HEADER_HEIGHT = 32;

  #hasHeader(row: SheetRow.Joined.Handle): boolean {
    const hooks = row.graphHooks;
    return (
      !!row.formula ||
      !!Json.string(row.variant?.attributes() ?? null, "_formula") ||
      !!hooks?.adapter ||
      !!hooks?.groupAdapter
    );
  }

  async bindCell(
    $cell: DataSeriesCell,
    { sheet: rowList, row, column }: BindCellContext,
  ) {
    const color = Json.string(row.variant?.attributes() ?? null, "color") ?? "var(--blue)";

    // Offset cell below static header when present
    $cell.style.setProperty(
      "--header-offset",
      this.#hasHeader(row) ? ColumnSeriesSummaryRendererPlugin.#HEADER_HEIGHT + "px" : "0px",
    );

    const [pd, tz] = column.discrete();
    const range = pd.toRange();
    const dtr = new DateTime.Range(
      tz.toWallClock(range.start.withTime(TimeOfDay.ZERO)),
      tz.toWallClock(range.end.withTime(TimeOfDay.ZERO)),
    );

    // The column's date-keyed cells over its days, its timepoint cells over
    // the same days in the column's zone.
    const dataPromise = Promise.all([row.cells(range), row.cells(dtr.toUtc())]);
    const stillValid = this.#stateManager.bind($cell, dataPromise);

    dataPromise
      .then(([dated, timed]) => {
        if (!stillValid(dataPromise)) return;
        const entries = dated.filter((c) => c.kind === "date");
        const customEntries = timed.filter((c) => c.kind === "timepoint");
        $cell.bind({ entries, customEntries, color, pd, tz });
      })
      .catch(() => {});
  }

  onBindRow(handle: SheetDelegate, row: SheetRow.Joined.Handle): void {
    const formula = row.formula ??
      Json.string(row.variant?.attributes() ?? null, "_formula");
    const hooks = row.graphHooks;
    const hasAdapter = !!hooks?.adapter;
    const hasGroupAdapter = !!hooks?.groupAdapter;

    if (!formula && !hasAdapter && !hasGroupAdapter) return;

    const $prev = handle.staticChild.get(
      "series-info",
      row.id,
    ) as Option<DataSeriesHeader>;
    const $header = $prev || DataSeriesHeader.make();

    const label = formula
      ? formula
      : hasGroupAdapter
        ? "group-adapter"
        : "adapter";

    $header.bind({ label, isFormula: !!formula });
    $header.bindHeight(ColumnSeriesSummaryRendererPlugin.#HEADER_HEIGHT);

    handle.staticChild.reveal("series-info", $header, row.id, {
      fillWidth: true,
    });
  }

  legendFactory: SheetLegendClass<SheetLegend.DefaultRow> = class {
    static make(): SheetLegend.DefaultRow {
      return SheetLegend.DefaultRow.make();
    }

    static bind(
      $legend: SheetLegend.DefaultRow,
      row: SheetRow.Joined.Handle,
      handle: SheetDelegate,
    ): void {
      $legend.bind(
        row,
        (target) => handle.rebind.revealRowOptionsPopover(row, target),
        handle,
      );
    }

    static computeWidth = SheetLegend.DefaultFactory.computeWidth;
  };

  loadData = null;

  optionsInfo(_row: SheetRow.Joined.Handle): Option<string> {
    return null;
  }

  onResize(_$cell: DataSeriesCell, _context: BindCellContext): void {}
  onViewportColumnsChanged(): void {}
  onRenderedColumnsWillChange(): void {}
}

/** Types that go with `ColumnSeriesSummaryRendererPlugin`: `Attributes`. */
export namespace ColumnSeriesSummaryRendererPlugin {
  export type Attributes = {
    source?: string;
    seriesKey?: string;
    seriesType?: string;
    color?: string;
    _formula?: string;
  };
}

/** One cell of a series summary: the entries of a column. */
@component({
  name: "std-data-series-cell",
})
export class DataSeriesCell extends LonaWebComponent {
  #$entries: SlotRow = this.hydrate("entries");

  bind(props: {
    entries: CellEntry[];
    customEntries: TimepointCellEntry[];
    color: string;
    pd: PartialDate;
    tz: TimezoneRegion;
  }): DataSeriesCell {
    const total = props.entries.length + props.customEntries.length;
    if (total === 0) {
      this.#$entries.bind(0, () => {});
      return this;
    }

    const { pd, tz } = props;

    // Build value map keyed by slot identifier
    const valueMap = new Map<number, unknown>();
    if (pd.type === "year") {
      for (const e of props.entries) valueMap.set(e.date.start.month1, e.value);
      for (const e of props.customEntries) {
        valueMap.set(tz.toTz(e.dtr.start).date.month1, e.value);
      }
    } else {
      for (const e of props.entries) valueMap.set(e.date.start.dse, e.value);
      for (const e of props.customEntries) {
        valueMap.set(tz.toTz(e.dtr.start).date.dse, e.value);
      }
    }

    // Generate slot keys matching header sub-period layout
    let slotKeys: number[];
    if (pd.type === "year") {
      slotKeys = [];
      for (let m = 1; m <= 12; m++) slotKeys.push(m);
    } else {
      const dates = pd.toRange().iter();
      slotKeys = [];
      for (const d of dates) slotKeys.push(d.dse);
    }

    this.#$entries.bind(slotKeys.length, (i, $card) => {
      $card.toggleAttribute("always-bg", true);
      const key = nonnull(slotKeys[i], "a summary card per slot");
      if (valueMap.has(key)) {
        $card.bind(formatSummaryValue(valueMap.get(key)), "", false);
      } else {
        $card.bind("", "", false);
        $card.style.visibility = "hidden";
      }
    });

    return this;
  }

  static $styles: Style.Compat[] = [
    css`
      :host {
        display: flex;
        align-items: center;
        height: calc(100% - var(--header-offset, 0px));
        margin-top: var(--header-offset, 0px);
        overflow: hidden;
        margin-inline: 3px;
        width: calc(100% - 6px);
        background: var(--bg-surface-light);
        border-radius: 8px;
      }

      #entries {
        height: calc(100% - 2px);
        margin-block: 1px;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <std-slot-row id="entries"></std-slot-row>
  `;
}

/** The header of a series summary: its label. */
@component({
  name: "std-data-series-header",
})
export class DataSeriesHeader extends LonaWebComponent {
  #$label: HTMLElement = this.$("label");

  bind(props: { label: string; isFormula: boolean }): DataSeriesHeader {
    this.#$label.textContent = props.label;
    this.toggleAttribute("formula", props.isFormula);
    return this;
  }

  bindHeight(heightPx: number) {
    $$.measure(() => {
      const height = this.offsetHeight;
      $$.mutate(() => {
        if (heightPx === height) return;
        this.style.height = heightPx + "px";
      });
    });
  }

  static $styles: Style.Compat[] = [
    css`
      #root {
        align-items: center;
        border-radius: 8px;
        padding: 8px 12px;
        background: var(--bg-surface-light);
        margin-left: 3px;
        width: calc(100% - 6px);
        overflow: hidden;
        height: calc(100% - 2px);
        margin-block: 1px;
      }

      #label {
        color: var(--text-sec);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        align-self: center;
      }

      :host([formula]) #label {
        font-family: monospace;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <std-row id="root">
      <p id="label" class=small></p>
    </std-row>
  `;
}

/** A summary cell's short text for one series value. */
function formatSummaryValue(v: unknown): string {
  if (v == null) return "\u2014";
  if (typeof v === "number") return String(v);
  if (typeof v === "string")
    return v.length > 6 ? v.slice(0, 5) + "\u2026" : v;
  if (typeof v === "boolean") return v ? "\u2713" : "\u2717";
  if (typeof v === "object")
    return `{${Object.keys(v as Record<string, unknown>).length}}`;
  return "\u2014";
}
